import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client/react';
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  buildScoringContext,
  evaluateMatches,
  normalizeEmail,
  normalizeEmails,
  recommendedMatches,
  topScoredMatches,
} from '@realadvisor/constants/source-match';
import {
  CircleCheckIcon,
  CircleIcon,
  ExternalLinkIcon,
  MailCheckIcon,
} from 'lucide-react';

import { SourceSystemSearch } from '@modules/organisations/components/SourceSystemSearch';
import {
  GET_CLOSE_LEAD,
  SEARCH_CLOSE_LEADS,
  SEARCH_STRIPE_CUSTOMERS,
} from '@modules/organisations/organisationsQueries';
import { useLocale } from '@shared/hooks/locale';

export type SourceMatch = {
  id: string;
  name?: string | null;
  email?: string | null;
  status?: string | null;
  url: string;
  matchedBy?: string;
};

// A Close lead's `name` is sometimes a raw tracking URL (the lead was created
// from a web form), e.g. "https://realadvisor.ch/de?utm_source=google&…". Show
// just the host so the card stays readable instead of dumping the query string.
// Plain names (and bare domains) are returned untouched.
const cleanName = (value?: string | null): string | null => {
  const trimmed = value?.trim();
  if (trimmed == null || trimmed === '') {
    return null;
  }
  if (!/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }
  try {
    return new URL(trimmed).hostname.replace(/^www\./, '') || trimmed;
  } catch {
    return trimmed;
  }
};

const MatchCard = ({
  source,
  match,
  selected,
  recommended,
  isSet,
  emailMatch,
  onClick,
}: {
  source: 'stripe' | 'close';
  match: SourceMatch;
  selected: boolean;
  recommended: boolean;
  /** The id is the one already persisted on the org (the current saved link). */
  isSet: boolean;
  /** The candidate's email exactly matches one of the org/team contacts. */
  emailMatch: boolean;
  onClick: () => void;
}) => {
  const { t } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const email = match.email?.trim();
  const status = match.status?.trim();
  // A persisted link reads as "confirmed" (success), a mere selection as
  // "chosen" (primary) — so the saved card stands apart from new picks.
  const accent = isSet ? palette.success.main : palette.primary.main;

  return (
    <Paper
      variant="outlined"
      onClick={onClick}
      sx={{
        p: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        cursor: 'pointer',
        borderRadius: 2,
        borderColor: selected ? accent : 'divider',
        bgcolor: selected
          ? `color-mix(in srgb, ${accent} ${isSet ? 8 : 4}%, transparent)`
          : 'background.paper',
        transition: 'border-color 120ms ease, background-color 120ms ease',
        '&:hover': {
          borderColor: selected ? accent : 'grey.400',
        },
      }}
    >
      {selected ? (
        <CircleCheckIcon
          color={'var(--mui-palette-common-white)'}
          fill={accent}
          size={18}
        />
      ) : (
        <CircleIcon color={'var(--mui-palette-grey-400)'} size={18} />
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
            {cleanName(match.name) || match.email?.trim() || match.id}
          </Typography>
          {isSet ? (
            <Chip
              label={t('Set')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'success.main',
                bgcolor: `color-mix(in srgb, ${palette.success.main} 12%, transparent)`,
              }}
            />
          ) : recommended ? (
            <Chip
              label={t('Recommended')}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'primary.main',
                bgcolor: `color-mix(in srgb, ${palette.primary.main} 10%, transparent)`,
              }}
            />
          ) : null}
        </Stack>
        {email != null && email !== '' && (
          <Stack
            direction="row"
            sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}
          >
            {emailMatch && (
              <MailCheckIcon
                style={{ flexShrink: 0 }}
                size={'0.9rem'}
                color={'var(--mui-palette-success-main)'}
              />
            )}
            <Typography
              variant="caption"
              noWrap
              sx={{
                display: 'block',
                color: emailMatch ? 'success.main' : 'text.secondary',
                fontWeight: emailMatch ? 600 : 400,
              }}
            >
              {email}
            </Typography>
          </Stack>
        )}
        {source === 'close' && status != null && status !== '' && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block' }}
          >
            {status}
          </Typography>
        )}
        {source === 'stripe' && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ display: 'block' }}
          >
            {`${t('Customer ID')}: ${match.id}`}
          </Typography>
        )}
      </Box>
      <Tooltip
        title={source === 'stripe' ? t('Open in Stripe') : t('Open in Close')}
        arrow
      >
        <Link
          href={match.url}
          target="_blank"
          rel="noopener noreferrer"
          // Stop the card's onClick so opening the record never toggles the
          // selection.
          onClick={event => event.stopPropagation()}
          sx={{
            display: 'inline-flex',
            color: 'text.secondary',
            '&:hover': { color: 'primary.main' },
          }}
        >
          <ExternalLinkIcon size={18} />
        </Link>
      </Tooltip>
    </Paper>
  );
};

type MatchSuggestionFieldProps = {
  source: 'stripe' | 'close';
  label: string;
  searchPlaceholder: string;
  suggestedQueries: string[];
  selected: SourceMatch[];
  onAdd: (match: SourceMatch) => void;
  onRemove: (id: string) => void;
  /** When false (default) selecting a match replaces the current selection. */
  multiple?: boolean;
  /**
   * The org being edited. Passed to the search so candidates already linked to
   * *other* orgs are filtered out (this org's own links stay visible). Omitted
   * on creation → every already-assigned id is excluded.
   */
  organisationId?: string;
  /**
   * The org's + its team members' email addresses. A candidate whose email
   * exactly matches one of these is recommended outright — the billing account
   * provably belongs to that person, even when the name reads as a generic
   * franchise label. Both Stripe and Close candidates now carry an email, so
   * this drives email authority for either source.
   */
  knownEmails?: ReadonlyArray<string | null | undefined>;
  /**
   * Ids already persisted on the org (the current saved Stripe/Close links).
   * Cards matching these are styled as "Set" to distinguish a confirmed link
   * from a new suggestion the user is about to add.
   */
  savedIds?: ReadonlyArray<string | null | undefined>;
};

export const MatchSuggestionField = ({
  source,
  label,
  searchPlaceholder,
  suggestedQueries,
  selected,
  onAdd,
  onRemove,
  multiple = false,
  organisationId,
  knownEmails,
  savedIds,
}: MatchSuggestionFieldProps) => {
  const { t } = useLocale();
  const [matches, setMatches] = useState<SourceMatch[]>([]);
  const [searching, setSearching] = useState(false);
  const [showOthers, setShowOthers] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  // Ids we auto-selected (so we can retract them when they stop being
  // recommended, e.g. once the website demotes a brand-only false match) and
  // ids the user removed by hand (so an auto-reconcile never re-adds them).
  const autoAddedIds = useRef<Set<string>>(new Set());
  const userRemovedIds = useRef<Set<string>>(new Set());

  // One Apollo client, not `useLazyQuery`: the search fires every query
  // concurrently (`Promise.all` below) and a lazy-query execute fn isn't safe to
  // call in parallel (the calls clobber each other's `.data`). `client.query`
  // runs each independently.
  const client = useApolloClient();

  // Linked matches hydrated from saved ids only carry `{ id, url }`, so resolve
  // their display name from Stripe/Close. (Temporary: see component note — we'd
  // rather persist the name at link time than refetch it on every open.)
  const [enriched, setEnriched] = useState<Record<string, SourceMatch>>({});
  const enrichAttempted = useRef<Set<string>>(new Set());

  // Key enrichment on the selection's id *content*, not the array reference:
  // callers rebuild `selected` inline every render, so keying on the array
  // cancels the in-flight name lookup constantly (the "loads only on reopen" bug).
  const selectedEnrichKey = selected
    .map(item => item.id)
    .sort()
    .join('\u0001');

  useEffect(() => {
    const missing = selected.filter(
      match =>
        (match.name == null || match.name === '') &&
        !enrichAttempted.current.has(match.id),
    );
    if (missing.length === 0) {
      return;
    }
    missing.forEach(match => enrichAttempted.current.add(match.id));

    let cancelled = false;
    void (async () => {
      const resolved: Record<string, SourceMatch> = {};
      await Promise.all(
        missing.map(async match => {
          try {
            if (source === 'stripe') {
              const { data } = await client.query({
                query: SEARCH_STRIPE_CUSTOMERS,
                variables: { query: match.id, limit: 1 },
                errorPolicy: 'all',
              });
              const list = data?.search_stripe_customers?.data;
              const found =
                list?.find(item => item.id === match.id) ?? list?.[0];
              if (found != null) {
                resolved[match.id] = {
                  ...match,
                  name: found.name,
                  email: found.email,
                };
              }
            } else {
              const { data } = await client.query({
                query: GET_CLOSE_LEAD,
                variables: { leadId: match.id },
                errorPolicy: 'all',
              });
              const lead = data?.get_close_lead;
              if (lead != null) {
                resolved[match.id] = {
                  ...match,
                  name: lead.name,
                  email: lead.email,
                };
              }
            }
          } catch {
            // A single failed enrichment must not sink the others.
          }
        }),
      );
      if (!cancelled && Object.keys(resolved).length > 0) {
        setEnriched(prev => ({ ...prev, ...resolved }));
      }
    })();

    return () => {
      cancelled = true;
    };
    // `selected` is read via closure; we intentionally key on its id content
    // (`selectedEnrichKey`) to avoid re-running on every inline-array rebuild.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEnrichKey, source, client]);

  const getResults = useCallback(
    async (query: string): Promise<SourceMatch[]> => {
      const trimmed = query.trim();
      if (trimmed.length < 2) {
        return [];
      }
      try {
        if (source === 'stripe') {
          const { data } = await client.query({
            query: SEARCH_STRIPE_CUSTOMERS,
            variables: { query: trimmed, limit: 10, organisationId },
            errorPolicy: 'all',
          });
          return (data?.search_stripe_customers?.data ?? []) as SourceMatch[];
        }
        const { data } = await client.query({
          query: SEARCH_CLOSE_LEADS,
          variables: { query: trimmed, limit: 10, organisationId },
          errorPolicy: 'all',
        });
        return (data?.search_close_leads?.data ?? []) as SourceMatch[];
      } catch {
        return [];
      }
    },
    [client, source, organisationId],
  );

  // `suggestedQueries` is rebuilt inline every render; key the memo on its string
  // content (not array identity) so the fetch effect doesn't re-fire and cancel
  // its debounce every render (which left the spinner stuck on busy queries).
  const suggestedKey = suggestedQueries
    .map(q => q.trim())
    .filter(Boolean)
    .join('\u0001');
  const normalizedQueries = useMemo(
    () =>
      Array.from(new Set(suggestedKey.split('\u0001'))).filter(
        q => q.length >= 2,
      ),
    [suggestedKey],
  );

  // URLs are kept for retrieval (backend domain matching) but dropped from
  // scoring; names are expanded into entity segments locally and the website
  // domain yields the brand tokens to neutralise, so the *person/company*, not
  // the shared platform brand, decides what is recommended.
  const { scoringQueries, brandTokens } = useMemo(
    () => buildScoringContext(normalizedQueries),
    [normalizedQueries],
  );

  // Callers pass `knownEmails` inline too, so key the set on its content (same
  // reasoning as `suggestedKey` above) to keep `evaluate` stable across renders.
  const knownEmailsKey = normalizeEmails(knownEmails ?? [])
    .sort()
    .join('\u0001');
  const knownEmailSet = useMemo(
    () => new Set(knownEmailsKey.split('\u0001').filter(Boolean)),
    [knownEmailsKey],
  );

  const savedIdKey = (savedIds ?? [])
    .filter((id): id is string => id != null && id !== '')
    .join('\u0001');
  const savedIdSet = useMemo(
    () => new Set(savedIdKey.split('\u0001').filter(Boolean)),
    [savedIdKey],
  );
  // A field that already has a saved link should not spend Stripe/Close calls
  // auto-searching on open — show the set card and let the user opt into a
  // search via the button below.
  const hasSavedLink = savedIdSet.size > 0;
  const [searchRequested, setSearchRequested] = useState(false);

  // Score a candidate set against the current queries with corpus-aware
  // weights. One place builds the IDF + evaluates, so retrieval sorting and
  // recommendation always agree on a candidate's standing.
  const evaluate = useCallback(
    (list: SourceMatch[]) =>
      evaluateMatches(list, scoringQueries, brandTokens, knownEmailSet),
    [scoringQueries, brandTokens, knownEmailSet],
  );

  const pickRecommended = useCallback(
    (list: SourceMatch[]): SourceMatch[] => {
      const recommended = recommendedMatches(evaluate(list));
      // Several historical accounts can match at once in multi-mode; single
      // mode keeps at most the top confident one and never force-fills.
      return multiple ? recommended : recommended.slice(0, 1);
    },
    [multiple, evaluate],
  );

  useEffect(() => {
    if (normalizedQueries.length === 0 || (hasSavedLink && !searchRequested)) {
      // Clearing the query while a search is in flight must drop the spinner,
      // otherwise "Finding matches…" stays stuck with no results behind it.
      // A field with a saved link stays idle until the user asks to search.
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        // Fetch every query in parallel — each is a Hasura-action → Stripe/Close
        // round-trip and they don't depend on each other. A single failing
        // query must not sink the others, so isolate each with a catch.
        const perQuery = await Promise.all(
          normalizedQueries.map(async query => ({
            query,
            results: await getResults(query).catch(() => [] as SourceMatch[]),
          })),
        );
        if (cancelled) {
          return;
        }
        const byId = new Map<string, SourceMatch>();
        for (const { query, results } of perQuery) {
          for (const result of results) {
            if (!byId.has(result.id)) {
              byId.set(result.id, { ...result, matchedBy: query });
            }
          }
        }
        const found = topScoredMatches(evaluate([...byId.values()]));
        setMatches(found);
        // Reconcile the auto-selection with the current recommendations: add
        // newly recommended rows and retract auto-added rows that no longer
        // qualify, never touching the user's own picks/removals.
        const recommended = pickRecommended(found);
        const recommendedIdSet = new Set(recommended.map(item => item.id));
        const selectedIdSet = new Set(selected.map(item => item.id));
        // Single-mode fields hold one value; only auto-fill them while empty so
        // we never fight a manual choice. Multi-mode can keep reconciling.
        if (multiple || selected.length === 0) {
          for (const match of recommended) {
            if (
              !selectedIdSet.has(match.id) &&
              !userRemovedIds.current.has(match.id)
            ) {
              autoAddedIds.current.add(match.id);
              onAdd(match);
            }
          }
        }
        for (const id of [...autoAddedIds.current]) {
          if (!recommendedIdSet.has(id) && selectedIdSet.has(id)) {
            autoAddedIds.current.delete(id);
            onRemove(id);
          }
        }
      } finally {
        // Always clear the spinner, even on error/empty — never leave it stuck.
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getResults, normalizedQueries, hasSavedLink, searchRequested]);

  const recommendedIds = useMemo(
    () => new Set(pickRecommended(matches).map(match => match.id)),
    [matches, pickRecommended],
  );
  const selectedIds = useMemo(
    () => new Set(selected.map(item => item.id)),
    [selected],
  );
  const matchesById = useMemo(
    () => new Map(matches.map(match => [match.id, match])),
    [matches],
  );
  const otherMatches = useMemo(
    () => matches.filter(match => !selectedIds.has(match.id)),
    [matches, selectedIds],
  );

  // A saved link only persists its id, so a freshly-opened card starts nameless.
  // Resolve the display fields from the live search results first (they always
  // carry names) and the by-id enrichment second, so a linked card never shows a
  // bare `cus_/lead_` id while a name for it is already in hand.
  const resolveDisplay = (match: SourceMatch): SourceMatch => {
    const fromMatches = matchesById.get(match.id);
    const fromEnriched = enriched[match.id];
    const pick = (key: 'name' | 'email' | 'status') =>
      fromEnriched?.[key]?.trim() ||
      fromMatches?.[key]?.trim() ||
      match[key]?.trim() ||
      null;
    return {
      ...match,
      name: pick('name'),
      email: pick('email'),
      status: pick('status'),
    };
  };

  // A candidate whose email is one of the org/team contacts is the same
  // person/company — the card flags it so the user can trust the suggestion.
  const isEmailMatch = (match: SourceMatch): boolean => {
    const email = normalizeEmail(match.email);
    return email != null && knownEmailSet.has(email);
  };

  const handlePick = (match: SourceMatch) => {
    userRemovedIds.current.delete(match.id);
    if (!multiple) {
      for (const item of selected) {
        if (item.id !== match.id) {
          userRemovedIds.current.add(item.id);
          autoAddedIds.current.delete(item.id);
          onRemove(item.id);
        }
      }
    }
    onAdd(match);
  };

  // Manual removal is sticky: remember it so the auto-reconcile won't re-add it.
  const handleRemove = (id: string) => {
    userRemovedIds.current.add(id);
    autoAddedIds.current.delete(id);
    onRemove(id);
  };

  const busyRow = (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1, py: 0.5 }}>
      <CircularProgress size={14} />
      <Typography variant="caption" color="text.secondary">
        {t('Finding matches…')}
      </Typography>
    </Stack>
  );

  // Selected cards resolve their display fields (a saved link starts nameless);
  // other-match cards already carry names from the live search.
  const renderCard = (match: SourceMatch, isSelected: boolean) => {
    const resolved = isSelected ? resolveDisplay(match) : match;
    return (
      <MatchCard
        key={match.id}
        source={source}
        match={resolved}
        selected={isSelected}
        recommended={recommendedIds.has(match.id)}
        isSet={savedIdSet.has(match.id)}
        emailMatch={isEmailMatch(resolved)}
        onClick={() =>
          isSelected ? handleRemove(match.id) : handlePick(match)
        }
      />
    );
  };

  return (
    <Stack spacing={1}>
      <Stack
        direction="row"
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Typography variant="subtitle2">{label}</Typography>
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          onClick={() => setShowSearch(value => !value)}
          sx={{ fontWeight: 600 }}
        >
          {t('Search another')}
        </Link>
      </Stack>

      {showSearch && (
        <SourceSystemSearch
          source={source}
          placeholder={searchPlaceholder}
          organisationId={organisationId}
          onSelect={match => {
            handlePick(match as SourceMatch);
            setShowSearch(false);
          }}
        />
      )}

      {searching && selected.length === 0 && matches.length === 0 ? (
        busyRow
      ) : (
        <Stack spacing={1}>
          {selected.map(match => renderCard(match, true))}

          {hasSavedLink && !searchRequested && (
            <Link
              component="button"
              type="button"
              variant="caption"
              underline="hover"
              onClick={() => setSearchRequested(true)}
              sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
            >
              {t('Search for more matches')}
            </Link>
          )}

          {hasSavedLink && searchRequested && searching && busyRow}

          {selected.length === 0 && otherMatches.length === 0 && (
            <Typography variant="caption" color="text.secondary">
              {t('No suggested matches found')}
            </Typography>
          )}

          {otherMatches.length > 0 && (
            <>
              <Link
                component="button"
                type="button"
                variant="caption"
                underline="hover"
                onClick={() => setShowOthers(value => !value)}
                sx={{ alignSelf: 'flex-start', fontWeight: 600 }}
              >
                {showOthers
                  ? t('Hide other matches')
                  : t('Show {{count}} other matches', {
                      count: otherMatches.length,
                    })}
              </Link>
              <Collapse in={showOthers} unmountOnExit>
                <Stack spacing={1}>
                  {otherMatches.map(match => renderCard(match, false))}
                </Stack>
              </Collapse>
            </>
          )}
        </Stack>
      )}
    </Stack>
  );
};
